/**
 * Market-aware money formatting for scripts that render prices client-side.
 *
 * Amounts are not the problem — Liquid and the Ajax API both hand us the
 * customer's presentment currency already. The *format* is what JS can't see:
 * Shopify.formatMoney and shop.money_format follow the shop's default currency,
 * so anything built on them prints "zł" to a customer browsing in CZK.
 *
 * So the layout publishes two samples rendered by Liquid's own `money` filter —
 * money(0) and money(1000.00) — on <body>, and we read the pattern back out of
 * them. Whatever the merchant configured per currency, the output matches the
 * prices Liquid renders elsewhere on the page.
 */
window.WingsMoney = (function () {
  /**
   * Derives a formatter from the two samples. Returns null when they carry no
   * digits, so callers can fall back.
   */
  function createMoneyFormatter(zeroSample, thousandSample) {
    function parts(sample) {
      var s = String(sample == null ? '' : sample);
      var first = s.search(/\d/);
      if (first === -1) return null;
      var last = s.length - 1;
      while (last >= 0 && !/\d/.test(s.charAt(last))) last--;
      return { prefix: s.slice(0, first), body: s.slice(first, last + 1), suffix: s.slice(last + 1) };
    }

    var zero = parts(zeroSample);
    if (!zero) return null;

    // "0,00" -> "," and 2 decimals; "0" -> no decimals at all (JPY and friends).
    var decimalSep = '';
    var decimals = 0;
    var split = zero.body.match(/^\d+(\D)(\d+)$/);
    if (split) {
      decimalSep = split[1];
      decimals = split[2].length;
    }

    // Strip the decimals off 1000.00 and "1<sep>000" is left — whatever sits
    // between the digits is the thousands separator (empty when ungrouped).
    var thousandsSep = '';
    var thousand = parts(thousandSample);
    if (thousand) {
      var grouped = thousand.body;
      if (decimals) grouped = grouped.slice(0, grouped.length - decimals - decimalSep.length);
      thousandsSep = grouped.replace(/\d/g, '');
    }

    return function formatMoney(cents) {
      var amount = (Number(cents) || 0) / 100;
      var sign = amount < 0 ? '-' : '';
      var fixed = Math.abs(amount).toFixed(decimals);
      var dot = fixed.indexOf('.');
      var whole = dot === -1 ? fixed : fixed.slice(0, dot);
      var fraction = dot === -1 ? '' : fixed.slice(dot + 1);
      if (thousandsSep) {
        whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, function () { return thousandsSep; });
      }
      return sign + zero.prefix + whole + (decimals ? decimalSep + fraction : '') + zero.suffix;
    };
  }

  // Money formats may contain HTML entities (a merchant-typed &nbsp; between
  // amount and symbol), so decode before deriving separators from them.
  function decodeSample(raw) {
    if (!raw) return '';
    var decoder = document.createElement('textarea');
    decoder.innerHTML = raw;
    return decoder.value;
  }

  // Only reached when the samples are missing. Intl may group or place the
  // symbol slightly differently than the money filter, but it never shows the
  // wrong currency.
  function fallbackFormatter() {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    return function (cents) {
      var amount = (Number(cents) || 0) / 100;
      if (code && typeof Intl !== 'undefined' && Intl.NumberFormat) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: code
          }).format(amount);
        } catch (err) {
          // Unknown currency code — fall through to the bare number.
        }
      }
      return amount.toFixed(2);
    };
  }

  /**
   * Formatter for the currency in effect around `element`. Reads the nearest
   * ancestor carrying the samples, so a section can scope its own if it ever
   * needs to; otherwise it lands on the pair published by the layout.
   */
  function formatterFor(element) {
    var source = element && element.closest ? element.closest('[data-money-zero]') : null;
    if (!source) source = document.querySelector('[data-money-zero]');
    if (source) {
      var formatter = createMoneyFormatter(
        decodeSample(source.getAttribute('data-money-zero')),
        decodeSample(source.getAttribute('data-money-thousand'))
      );
      if (formatter) return formatter;
    }
    return fallbackFormatter();
  }

  return {
    createMoneyFormatter: createMoneyFormatter,
    formatterFor: formatterFor
  };
})();